/**
 * Frontend Service Provider for Gateway Communication
 *
 * This service handles all communication between the frontend and the Gateway service.
 *
 * NOTE: This app is moving to a multi-tenant model where EVERY call is tenant-scoped via
 * `?domain=<tenant.domain>` and where secrets must never be exposed to the browser.
 */

import axios from 'axios';
import {
  getTenantDomain,
  setTenantData as persistTenantData,
  getTenantData as readPersistedTenantData,
  clearTenantData as clearPersistedTenantData,
  setLogoutRedirectUri as persistLogoutRedirectUri,
  getLogoutRedirectUri as readLogoutRedirectUri,
  clearTenantContext as clearPersistedTenantContext,
} from '../lib/tenantContext';

/**
 * Chat attachment limits — client-side preview gate. The authoritative
 * limits live server-side in `config/media.php` (limits.chat_*); these
 * mirror the defaults so the UI can reject obvious oversize / wrong-type
 * picks before bothering the gateway. Keep them in sync when defaults
 * change in the gateway config.
 */
export const CHAT_ATTACHMENT_LIMITS = {
  photo: {
    maxBytes: 8 * 1024 * 1024, //  8 MB
    mimes: ['image/jpeg', 'image/png', 'image/webp'],
  },
  video: {
    maxBytes: 25 * 1024 * 1024, // 25 MB
    mimes: ['video/mp4', 'video/webm', 'video/quicktime'],
  },
};

class FrontendService {
  constructor() {
    // In browser we want same-origin calls via Kong so:
    // - no mixed-content issues on https
    // - no CORS issues
    // - Kong can inject server-side secrets for Gateway auth
    const envBaseURL = process.env.NEXT_PUBLIC_API_BASE_URL;

    let baseURL = envBaseURL;
    if (typeof window !== 'undefined') {
      // Prefer same-origin Gateway prefix when running on https.
      // (If env points to http://..., browsers will block it as mixed content.)
      const isHttps = window.location?.protocol === 'https:';
      if (!baseURL || (isHttps && String(baseURL).startsWith('http://'))) {
        baseURL = '/gateway/api/v1';
      }
    }

    this.client = axios.create({
      baseURL,
      timeout: 15000,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });

    // Cache for tenant and user data
    this.cache = new Map();
    this.cacheTimeout = 30 * 60 * 1000; // 30 minutes

    // Setup interceptors
    this.setupRequestInterceptor();
    this.setupResponseInterceptor();
  }

  /* ====================================================================
   * Core Configuration & Setup
   * ==================================================================== */

  setupRequestInterceptor() {
    this.client.interceptors.request.use(
      (config) => {
        // Add auth token if available (allow caller to override)
        const token = this.getAuthToken();
        if (token && !config.headers?.Authorization) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Add request ID for tracking
        config.headers['X-Request-ID'] = this.generateRequestId();

        // Enforce tenant scoping by default.
        // If caller already set params.domain, do not override.
        const existingDomain = config?.params?.domain;
        if (!existingDomain) {
          const tenantDomain = getTenantDomain();
          if (tenantDomain) {
            config.params = {
              ...(config.params || {}),
              domain: tenantDomain,
            };
          }
        }

        return config;
      },
      (error) => Promise.reject(error)
    );
  }

  setupResponseInterceptor() {
    this.client.interceptors.response.use(
      (response) => {
        // Validate standard Gateway response format
        if (response.data && typeof response.data === 'object') {
          const { status, message, data, tenant, meta } = response.data;
          
          if (status !== undefined) {
            // Standard Gateway response - process it
            if (!status && message) {
              throw new FrontendServiceError(message, response.data, response.status);
            }
          }
        }
        
        return response;
      },
      (error) => {
        if (error.response?.status === 401) {
          // Only clear the session when the 401 came from a request that used
          // the main session token. Requests made with override tokens (masquerade
          // JWTs, etc.) must NOT invalidate the primary auth session on failure.
          const storedToken = this.getAuthToken();
          const requestAuth = error.config?.headers?.Authorization || '';
          const usedToken = requestAuth.startsWith('Bearer ')
            ? requestAuth.slice(7)
            : requestAuth;
          if (!storedToken || usedToken === storedToken) {
            this.handleUnauthorized();
          }
        }
        return Promise.reject(error);
      }
    );
  }

  /* ====================================================================
   * Authentication & Token Management  
   * ==================================================================== */

  async login(email, password) {
    try {
      const response = await this.client.post('/login', { email, password });

      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Login failed', response.data);
      }

      const { token, user } = response.data.data;

      // Store authentication data
      this.setAuthToken(token);
      this.setUserData(user);

      // Clear cache on login to ensure fresh data
      this.clearCache();

      return { token, user, tenant: response.data.tenant };
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Exchange an opaque SSO token for a Sanctum token + user session.
   * Requires tenant context to be resolved by Gateway (via ?domain=...).
   */
  async exchangeSsoToken(token, { domain } = {}) {
    try {
      const response = await this.client.post(
        '/sso/exchange',
        { token },
        { params: domain ? { domain } : undefined }
      );

      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'SSO exchange failed', response.data);
      }

      const { token: authToken, user } = response.data.data;

      this.setAuthToken(authToken);
      this.setUserData(user);

      // Some endpoints may also include tenant info at top-level.
      if (response.data.tenant) {
        this.setTenantData(response.data.tenant);
      }

      // Clear cache to ensure fresh data under the new session
      this.clearCache();

      return { token: authToken, user, tenant: response.data.tenant || null };
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  async logout() {
    try {
      await this.client.post('/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.clearAuthToken();
      this.clearUserData();
      clearPersistedTenantContext();
      clearPersistedTenantData();
      this.clearCache();
    }
  }

  async getCurrentUser() {
    const cacheKey = 'current_user';
    const cached = this.getFromCache(cacheKey);
    if (cached) return cached;

    try {
      const response = await this.client.get('/me');
      
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to get user', response.data);
      }
      
      const user = response.data.data.user;
      this.setInCache(cacheKey, user);
      
      return user;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /* ====================================================================
   * Tenant Resolution & Management
   * ==================================================================== */

  async resolveTenant(domain = null) {
    const cacheKey = `tenant_${domain || 'current'}`;
    const cached = this.getFromCache(cacheKey);
    if (cached) return cached;

    try {
      const params = domain ? { domain } : {};
      const response = await this.client.get('/tenant/resolve', { params });
      
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to resolve tenant', response.data);
      }
      
      const tenant = response.data.data.tenant;
      
      // Cache tenant data
      this.setInCache(cacheKey, tenant);
      this.setTenantData(tenant);
      
      return tenant;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /* ====================================================================
   * Chat & Conversations
   * ==================================================================== */

  /**
   * Search the cross-tenant chat directory.
   *
   * @param {object} opts
   * @param {string} [opts.q]      Free-text query (matched against name / email).
   * @param {number} [opts.limit]  Row cap (server enforces 1..100).
   * @param {('customers'|'providers')} [opts.target]
   *   Strict per-surface role filter. Pass `'customers'` from the /escort
   *   side to get only customer-role rows, `'providers'` from the /chat
   *   side to get only escort/performer/office rows. Omitting it keeps
   *   the legacy viewer-auto-detect behaviour, which is unreliable for
   *   accounts that hold multiple roles — always pass `target` from new
   *   surfaces.
   */
  async searchChatProviders({ q = '', limit = 50, target = null } = {}) {
    try {
      const params = {
        limit,
      };

      if (q && String(q).trim()) {
        params.q = String(q).trim();
      }

      if (target === 'customers' || target === 'providers') {
        params.target = target;
      }

      // Role filtering is enforced server-side based on the authenticated
      // user's role + the explicit `target` hint above.
      const response = await this.client.get('/chat/providers', { params });

      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to fetch chat providers', response.data);
      }

      return response.data.data.providers || [];
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  async getConversations() {
    const cacheKey = 'conversations';
    const cached = this.getFromCache(cacheKey, 5 * 60 * 1000); // 5 minutes cache
    if (cached) return cached;

    try {
      const response = await this.client.get('/conversations');
      
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to get conversations', response.data);
      }
      
      const conversations = response.data.data.conversations || [];
      this.setInCache(cacheKey, conversations);
      
      return conversations;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  async createConversation(participantId) {
    try {
      const response = await this.client.post('/conversations', {
        participant_id: participantId
      });

      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to create conversation', response.data);
      }

      // Clear conversations cache since we added a new one
      this.removeFromCache('conversations');

      return response.data.data.conversation;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Public discovery endpoint backing the chat-sidebar Discover tab.
   *
   * Wraps `GET /api/v1/listings` (gateway ListingController). The contract is
   * shared with the public listings page on tippelstreet.com so we forward
   * params verbatim — only `null` / `undefined` / empty-string entries are
   * stripped to keep the URL tidy.
   *
   * Common params:
   *   page, per_page, view ('list' | 'grid' | 'map')
   *   lat, lng, radius_km                       (center+radius)
   *   sw_lat, sw_lng, ne_lat, ne_lng            (bounding box, map view)
   *   q, type, services, ethnicities, ...       (rich filter set)
   *
   * Returns the raw `data` envelope: `{ items, pagination }`.
   */
  async getListings(params = {}) {
    // Coerce booleans to 1 / 0 because axios serialises JS `true` as the
    // string "true" in query strings, but Laravel's `boolean` validation
    // only accepts 1 / 0 / "1" / "0" / true / false (NOT "true" / "false").
    // Sending `have_photos=true` therefore returns 422 Validation failed,
    // even though the field is documented as boolean. Normalising here
    // keeps every future caller honest without shifting the burden onto
    // them.
    const cleanParams = {};
    for (const [key, value] of Object.entries(params || {})) {
      if (value === undefined || value === null) continue;
      if (typeof value === 'string' && value.trim() === '') continue;
      if (typeof value === 'boolean') {
        cleanParams[key] = value ? 1 : 0;
        continue;
      }
      cleanParams[key] = value;
    }

    try {
      const response = await this.client.get('/listings', { params: cleanParams });

      if (!response.data.status) {
        throw new FrontendServiceError(
          response.data.message || 'Failed to load listings',
          response.data
        );
      }

      return response.data.data || { items: [], pagination: {} };
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  async updateConversation(conversationId, patch) {
    try {
      const response = await this.client.patch(`/conversations/${conversationId}`, patch);

      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to update conversation', response.data);
      }

      // Clear conversations cache since an item may have changed
      this.removeFromCache('conversations');

      return response.data.data.conversation;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Helper used by UI to match a provider's identity (LiveKit participant identity)
   * to an escort/performer ID.
   */
  async findProviderIdByIdentity(identity, role = 'escort') {
    if (!identity) return null;

    const endpoint = role === 'performer' ? '/performers' : '/escorts';

    try {
      const response = await this.client.get(endpoint, {
        params: { identity }
      });

      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to lookup provider', response.data);
      }

      const payload = response.data.data || {};
      const providers = payload.escorts || payload.performers || [];
      const match = providers.find((p) => p.identity === identity || p.name === identity);
      return match?.id || null;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  async getMessages(conversationId, limit = 50, offset = 0) {
    try {
      const response = await this.client.get(`/conversations/${conversationId}/messages`, {
        params: { limit, offset }
      });

      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to get messages', response.data);
      }

      return (response.data.data.messages || []).reverse(); // Newest first in API, oldest first in UI
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  async getMessagesAs(authToken, conversationId, limit = 50, offset = 0) {
    try {
      const response = await this.client.get(`/conversations/${conversationId}/messages`, {
        params: { limit, offset },
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
      });

      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to get messages', response.data);
      }

      return (response.data.data.messages || []).reverse();
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Upload a chat attachment (photo or video) and receive a CDN URL.
   * The returned URL is what we then pass through `attachments` on
   * sendMessage(); the gateway already accepts a URL array and stores it
   * on the message row (see StoreMessageRequest).
   *
   * Limits and rate-limits are enforced server-side
   * (config/media.php → limits.chat_*, RateLimiter::for('chat-uploads')).
   * The constants below mirror the production defaults so the FE can
   * reject obviously oversized files before the upload begins, giving
   * the user instant feedback instead of a 422 round-trip.
   *
   * @param {File} file
   * @param {'photo'|'video'} kind
   * @param {string} conversationId
   * @param {(percent:number) => void} [onProgress]
   * @returns {Promise<{ url:string, kind:string, bytes:number, mime:string, asset_uuid:string, cdn_host:string }>}
   */
  async uploadChatAttachment(file, kind, conversationId, onProgress) {
    if (!file)            throw new FrontendServiceError('No file selected', null, 400);
    if (!conversationId)  throw new FrontendServiceError('Missing conversation_id', null, 400);

    const fd = new FormData();
    fd.append('file', file);
    fd.append('conversation_id', conversationId);
    if (kind === 'photo' || kind === 'video') fd.append('kind', kind);

    try {
      const response = await this.client.post('/me/chat-attachments', fd, {
        // Let the browser set the multipart boundary; do not override
        // Content-Type here. Axios reads `Content-Type` set on the request
        // config and will leave it alone when undefined.
        headers: { 'Content-Type': undefined },
        onUploadProgress: (e) => {
          if (typeof onProgress !== 'function' || !e?.total) return;
          onProgress(Math.round((e.loaded / e.total) * 100));
        },
        // Uploads can take longer than the default 15 s; allow 2 min for
         // a 25 MB video on a slow uplink.
        timeout: 120_000,
      });

      if (!response.data.status) {
        throw new FrontendServiceError(
          response.data.message || 'Failed to upload attachment',
          response.data
        );
      }
      return response.data.data.attachment;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  async sendMessage(conversationId, text, attachments = [], clientId = null) {
    try {
      const payload = {
        conversation_id: conversationId,
        text: text.trim(),
        attachments
      };

      if (clientId) {
        payload.client_id = clientId;
      }

      const response = await this.client.post('/messages', payload);

      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to send message', response.data);
      }

      return response.data.data.message;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Send a message as the authenticated operator, optionally attributing it to
   * a masqueraded escort.
   *
   * @param {string|null} actingAsEscortId - When the operator is masquerading,
   *   this is the escort's user ID. The gateway will set sender_id = escortId,
   *   operator_id = operatorId, and acting_as_escort_id = escortId on the
   *   message row so attribution is correct for billing and audit.
   *   Pass null for non-masquerade sends.
   */
  async sendMessageAs(authToken, conversationId, text, attachments = [], clientId = null, actingAsEscortId = null) {
    try {
      const payload = {
        conversation_id: conversationId,
        text: text.trim(),
        attachments
      };

      if (clientId) {
        payload.client_id = clientId;
      }

      // Include the masquerade escort ID so the gateway sets the correct
      // sender_id and operator attribution fields on the message row.
      if (actingAsEscortId) {
        payload.acting_as_escort_id = actingAsEscortId;
      }

      const response = await this.client.post('/messages', payload, {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
      });

      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to send message', response.data);
      }

      return response.data.data.message;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  async generateChatToken() {
    try {
      const response = await this.client.post('/chat/token');

      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to generate chat token', response.data);
      }

      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  async generateChatSubscriptionToken(conversationId) {
    try {
      const response = await this.client.post('/chat/subscription-token', {
        conversation_id: conversationId,
      });

      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to generate chat subscription token', response.data);
      }

      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  // ===================================================================
  // In-app notifications inbox
  // ===================================================================

  /**
   * Unread counts + by_category breakdown for the inbox badge / chips.
   * Backed by GET /me/notifications/summary. Cached 10s server-side.
   *
   * @returns {Promise<{ unread_total: number, by_category: object, by_group: object }>}
   */
  async getNotificationSummary() {
    try {
      const response = await this.client.get('/me/notifications/summary');
      if (!response.data.status) {
        throw new FrontendServiceError(
          response.data.message || 'Failed to load notification summary',
          response.data,
        );
      }
      return response.data.data || {};
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Paginated notifications feed. Filters: { unread, category, group, per_page }.
   * Backed by GET /me/notifications.
   */
  async listNotifications(params = {}) {
    try {
      const response = await this.client.get('/me/notifications', { params });
      if (!response.data.status) {
        throw new FrontendServiceError(
          response.data.message || 'Failed to load notifications',
          response.data,
        );
      }
      const body = response.data.data || {};
      return {
        data: Array.isArray(body.data) ? body.data : [],
        meta: body.meta || {},
      };
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Claim a bonus tied to a notification (welcome bonus, streak, win-back).
   * Backed by POST /me/notifications/{uuid}/claim. Idempotent server-side.
   *
   * @returns {Promise<{ id: string, claim_status: 'claimed', bonus_credits: number, wallet: object }>}
   */
  async claimNotificationBonus(uuid) {
    try {
      const response = await this.client.post(`/me/notifications/${encodeURIComponent(uuid)}/claim`);
      if (!response.data.status) {
        throw new FrontendServiceError(
          response.data.message || 'Failed to claim bonus',
          response.data,
        );
      }
      // Bust the wallet balance cache so the FE shows the new credits.
      try { this.removeFromCache?.('wallet_balance'); } catch (_) {}
      return response.data.data || {};
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Mark notifications read. Payload: { ids?, category?, group?, all? }.
   * Backed by POST /me/notifications/read.
   *
   * @returns {Promise<{ updated: number }>}
   */
  async markNotificationsRead(payload = {}) {
    try {
      const response = await this.client.post('/me/notifications/read', payload);
      if (!response.data.status) {
        throw new FrontendServiceError(
          response.data.message || 'Failed to mark notifications read',
          response.data,
        );
      }
      return response.data.data || {};
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Subscription token for the per-user Centrifugo notify channel
   * (`notify.user.{user_id}`). Used by ChatProvider to subscribe to
   * real-time inbox events.
   *
   * @returns {Promise<{ token: string, channel: string, expires_in: number }>}
   */
  async generateNotifyToken() {
    try {
      const response = await this.client.post('/me/notifications/ws-token');
      if (!response.data.status) {
        throw new FrontendServiceError(
          response.data.message || 'Failed to generate notify token',
          response.data,
        );
      }
      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Subscription token for the tenant-wide Centrifugo presence channel.
   * Backed by POST /chat/presence-token. Used once per WS session by
   * `ChatProvider.subscribeToTenantPresence` so the client can bind its
   * user_id into Centrifugo's presence map — which then powers the
   * Online tab + every `is_online` enrichment elsewhere.
   *
   * @returns {Promise<{ token: string, channel: string, expires_in: number }>}
   */
  async generateTenantPresenceToken() {
    try {
      const response = await this.client.post('/chat/presence-token');
      if (!response.data.status) {
        throw new FrontendServiceError(
          response.data.message || 'Failed to generate presence token',
          response.data,
        );
      }
      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * "Wie volgt mij" — paginated reverse view of the followers ledger.
   * Powers the escort-side Followers tab. Each row is enriched with avatar,
   * city and live presence (`is_online`) plus a `total_distinct` count
   * usable as a motivation header.
   *
   * @param {{ page?: number, perPage?: number }} [opts]
   * @returns {Promise<{ data: Array, meta: { current_page:number, last_page:number, per_page:number, total:number, total_distinct:number } }>}
   */
  async getMyFollowers({ page = 1, perPage = 25 } = {}) {
    try {
      const response = await this.client.get('/me/followers/users', {
        params: { page, per_page: perPage },
      });
      // FollowersController returns a flat envelope {data, meta} (no {status}
      // wrapper) so we just sanity-check the shape here.
      const data = Array.isArray(response.data?.data) ? response.data.data : [];
      const meta = response.data?.meta || {};
      return { data, meta };
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Tenant-wide live online users, role-filtered (customers see providers,
   * providers/office see customers). Powers the escort-side Online tab.
   *
   * @returns {Promise<{ users: Array, total: number }>}
   */
  async getOnlineUsers() {
    try {
      const response = await this.client.get('/me/online-users');
      if (!response.data.status) {
        throw new FrontendServiceError(
          response.data.message || 'Failed to load online users',
          response.data,
        );
      }
      const data = response.data.data || {};
      return {
        users: Array.isArray(data.users) ? data.users : [],
        total: Number.isFinite(data.total) ? data.total : 0,
      };
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Send a typing indicator for a conversation.
   *
   * @param {string}      conversationId   - UUID of the conversation.
   * @param {boolean}     isTyping         - true = typing_start, false = typing_stop.
   * @param {string|null} authToken        - Explicit Sanctum token (operator flow).
   *   When null the request interceptor supplies the token from localStorage
   *   (customer / escort flow). Operators must pass their token explicitly
   *   because they are not direct conversation participants and the gateway
   *   needs acting_as_escort_id to locate the conversation.
   * @param {number|null} actingAsEscortId - Escort's user ID (operator flow).
   *   The gateway uses this to find the conversation via escort_id instead of
   *   the operator's own user ID. Mirrors the sendMessageAs pattern.
   */
  async sendChatTyping(conversationId, isTyping = true, authToken = null, actingAsEscortId = null) {
    try {
      const payload = {
        conversation_id: conversationId,
        is_typing: !!isTyping,
      };
      if (actingAsEscortId) {
        payload.acting_as_escort_id = actingAsEscortId;
      }
      const response = await this.client.post('/chat/typing', payload, {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
      });

      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to send typing indicator', response.data);
      }

      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /* ====================================================================
   * Operator API
   * ==================================================================== */

  async getOperatorRoster(authToken = null) {
    try {
      const response = await this.client.get('/operators/roster', {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
      });

      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to fetch roster', response.data);
      }

      return response.data.data.escorts || [];
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  async getOperatorContacts(authToken = null, { escortId = null, limit = null, filter = null } = {}) {
    try {
      const params = {};
      if (escortId) params.escort_id = escortId;
      if (limit) params.limit = limit;
      if (filter) params.filter = filter;
      const response = await this.client.get('/operators/contacts', {
        params: Object.keys(params).length ? params : undefined,
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
      });

      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to fetch contacts', response.data);
      }

      return response.data.data.conversations || [];
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Explicitly claim a conversation on behalf of the calling operator.
   * POST /operators/conversations/{id}/claim
   * Returns the claim object including claimed_by_me, expires_at, expires_in_seconds.
   */
  async claimConversation(authToken, conversationId) {
    try {
      const response = await this.client.post(
        `/operators/conversations/${conversationId}/claim`,
        {},
        { headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined }
      );
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to claim conversation', response.data);
      }
      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Release the calling operator's active claim on a conversation.
   * DELETE /operators/conversations/{id}/claim
   * Returns the released claim object.
   */
  async releaseConversation(authToken, conversationId) {
    try {
      const response = await this.client.delete(
        `/operators/conversations/${conversationId}/claim`,
        { headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined }
      );
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to release conversation', response.data);
      }
      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  async startOperatorMasquerade(authToken, escortId, ttlMinutes = 30) {
    try {
      const response = await this.client.post(
        '/operators/masquerade/start',
        { escort_id: escortId, ttl_minutes: ttlMinutes },
        { headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined }
      );

      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to start masquerade', response.data);
      }

      return response.data.data.masquerade_token;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  // =========================================================================
  // Live-session lifecycle (request → accept / decline)
  // =========================================================================

  /**
   * Customer or operator requests a live or audio session.
   * POST /conversations/{id}/live/request
   * @param {string} conversationId
   * @param {'live'|'audio'} mode
   * @param {number|null} pricePerMin - cents per minute, optional
   */
  async requestLiveSession(conversationId, mode = 'live', pricePerMin = null) {
    try {
      const payload = { mode };
      if (pricePerMin != null) payload.price_per_min = pricePerMin;
      const response = await this.client.post(
        `/conversations/${conversationId}/live/request`,
        payload
      );
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to request live session', response.data);
      }
      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Escort accepts a pending live/audio request with an explicit mode choice.
   * POST /conversations/{id}/live/accept
   * @param {string} conversationId
   * @param {'audio_only'|'live_full_camera'|'live_no_camera'|'live_hidden_camera'|'live_filtered_camera'} mode
   * @param {{ customerMic?: boolean, customerCamera?: boolean }} [opts]
   *        Optional per-session overrides for what the customer may publish.
   *        Omitted fields fall back to the escort's persistent defaults
   *        (User::livePreferencesResolved). The server forces customerCamera
   *        to false for audio_only and live_no_camera regardless of input.
   * (escort only — Gateway enforces this; mode is validated server-side)
   */
  async acceptLiveSession(conversationId, mode, opts = undefined) {
    try {
      const payload = { mode };
      if (opts && typeof opts === 'object') {
        if (typeof opts.customerMic    === 'boolean') payload.customer_mic    = opts.customerMic;
        if (typeof opts.customerCamera === 'boolean') payload.customer_camera = opts.customerCamera;
      }
      const response = await this.client.post(
        `/conversations/${conversationId}/live/accept`,
        payload
      );
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to accept live session', response.data);
      }
      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Escort ends an active live session for all participants.
   * POST /conversations/{id}/live/end
   * Stops PPM billing atomically on the backend.
   * (escort only — Gateway enforces this)
   */
  async endLiveSession(conversationId) {
    try {
      const response = await this.client.post(`/conversations/${conversationId}/live/end`);
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to end session', response.data);
      }
      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Update the caller's room presence flag for an active live session.
   * POST /conversations/{id}/live/presence
   * Called when the user connects (present=true) or disconnects (present=false)
   * from the LiveKit room. Best-effort — failures do not affect billing.
   * @param {string}  conversationId
   * @param {boolean} present
   */
  async updateLiveSessionPresence(conversationId, present) {
    try {
      const response = await this.client.post(
        `/conversations/${conversationId}/live/presence`,
        { present: !!present }
      );
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to update presence', response.data);
      }
      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Fetch a short-lived LiveKit join token for the active live/audio session.
   * POST /conversations/{id}/live/join-token
   * Both escort and customer may call this once the session is accepted.
   * Returns { token, server_url, room_name, accepted_mode, role }
   */
  async getLiveSessionJoinToken(conversationId) {
    try {
      const response = await this.client.post(
        `/conversations/${conversationId}/live/join-token`
      );
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to get join token', response.data);
      }
      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Heartbeat a running PPM session so the gateway monitor (`ppm:monitor`)
   * does not finalize it as 'timeout'. Touches `last_beat_at = now()` and
   * returns the current meter info.
   *
   * POST /ppm/sessions/{id}/heartbeat
   *
   * Authorization (gateway-side):
   *   Only the payer (customer) may heartbeat their own session. The escort
   *   would receive 403 — callers must therefore gate this call to the
   *   customer role on the FE side too.
   *
   * Terminal-state behaviour:
   *   - 409 "Session already charged": the gateway already finalized this
   *     session (e.g. customer hit /live/end, or insufficient balance).
   *     Treat as a non-error signal to stop polling.
   *   - 401/403: auth/role mismatch — stop polling, do not surface as an
   *     unexpected error.
   *   - Other failures (network, 5xx): bubble through normalizeError so the
   *     caller can decide whether to retry on the next tick.
   *
   * @param {string} ppmSessionId
   * @returns {Promise<{ session: object, minutes_sofar: number, amount_sofar: number }>}
   */
  async heartbeatPpmSession(ppmSessionId) {
    try {
      const response = await this.client.post(
        `/ppm/sessions/${ppmSessionId}/heartbeat`,
        {}
      );
      if (!response.data.status) {
        throw new FrontendServiceError(
          response.data.message || 'Failed to heartbeat PPM session',
          response.data
        );
      }
      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Submit a WebRTC quality snapshot for the current live session. Used by
   * the WebRTCStatsSampler in LiveSessionRoom to feed the gateway's
   * `webrtc_session_stats` table for dashboarding + alerting.
   *
   * POST /webrtc/stats
   *
   * Authorization:
   *   Sanctum-authenticated user. Tenant + (optional) conversation
   *   participant check happens server-side in WebRtcStatsController.
   *
   * Failure mode:
   *   This is fire-and-forget telemetry. The endpoint MUST NEVER raise a
   *   user-visible error, so we deliberately catch everything and return
   *   `false` instead of re-throwing. The sampler will simply try again on
   *   the next 10-second tick.
   *
   * @param {{
   *   at?: number,
   *   room?: string|null,
   *   conversation_id?: string|null,
   *   role: 'escort'|'customer'|'operator',
   *   accepted_mode?: string|null,
   *   available_outgoing_bitrate?: number|null,
   *   tracks: Array<object>,
   * }} payload
   * @returns {Promise<boolean>} true on success, false otherwise.
   */
  async submitWebRtcStats(payload) {
    try {
      const response = await this.client.post('/webrtc/stats', payload);
      return !!response?.data?.status;
    } catch (_err) {
      // Swallow — telemetry must not surface to the user. The sampler
      // re-attempts on the next tick anyway, and rate-limiting (60/min)
      // is enforced server-side so we don't even need our own backoff.
      return false;
    }
  }

  // =========================================================================
  // Escort session-availability preferences
  // =========================================================================

  /**
   * Fetch the authenticated user's resolved live preferences.
   * Returns { text_chat_enabled, audio_enabled, live_enabled, allowed_live_modes }.
   */
  async getMyLivePreferences() {
    try {
      const response = await this.client.get('/me/live-preferences');
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to fetch preferences', response.data);
      }
      return response.data.data.preferences;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Save the authenticated user's live preferences (full replacement).
   * @param {{ text_chat_enabled: boolean, audio_enabled: boolean, live_enabled: boolean, allowed_live_modes: string[] }} preferences
   */
  async updateMyLivePreferences(preferences) {
    try {
      const response = await this.client.put('/me/live-preferences', preferences);
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to update preferences', response.data);
      }
      return response.data.data.preferences;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Escort declines a pending live/audio request.
   * POST /conversations/{id}/live/decline
   * (escort only — Gateway enforces this)
   */
  async declineLiveSession(conversationId) {
    try {
      const response = await this.client.post(
        `/conversations/${conversationId}/live/decline`
      );
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to decline live session', response.data);
      }
      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Customer (or operator) cancels their own pending live/audio request.
   * POST /conversations/{id}/live/cancel
   *
   * The escort path uses declineLiveSession instead. Cancel resets the
   * conversation to idle and emits a live_session.cancelled event.
   */
  async cancelLiveSession(conversationId) {
    try {
      const response = await this.client.post(
        `/conversations/${conversationId}/live/cancel`
      );
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to cancel live session', response.data);
      }
      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  // =========================================================================
  // Paid-conversation lifecycle
  // =========================================================================

  /**
   * Set a conversation to paid mode (operator / escort only).
   * Returns the updated payment-state fields from the gateway.
   */
  async setPaidConversation(conversationId) {
    try {
      const response = await this.client.post(`/conversations/${conversationId}/set-paid`);
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to set paid', response.data);
      }
      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Reset a conversation back to free mode (operator / escort only).
   */
  async setFreeConversation(conversationId) {
    try {
      const response = await this.client.post(`/conversations/${conversationId}/set-free`);
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to set free', response.data);
      }
      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Customer approves the paid transition for a conversation.
   */
  async approvePaidConversation(conversationId) {
    try {
      const response = await this.client.post(`/conversations/${conversationId}/approve-paid`);
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to approve paid mode', response.data);
      }
      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  async stopOperatorMasquerade(authToken, escortId) {
    try {
      const response = await this.client.post(
        '/operators/masquerade/stop',
        { escort_id: escortId },
        { headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined }
      );

      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to stop masquerade', response.data);
      }

      return true;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Fetch the calling operator's current active masquerade session from the
   * Phase 2A session table.
   *
   * Used on page load to restore masquerade state without requiring the
   * operator to manually click "Start session" again after a browser reload.
   *
   * Returns { session: { id, escort: { id, name }, started_at, expires_at } }
   * or { session: null } when no active session exists (including when the
   * MASQUERADE_SESSIONS_ENABLED flag is off on the gateway).
   *
   * Never throws for the "no session" case — callers should only expect a
   * throw on genuine network/auth errors.
   */
  async getCurrentMasqueradeSession(authToken = null) {
    try {
      const response = await this.client.get('/operators/masquerade/current', {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
      });
      // Both "found" and "not found" are valid 200 responses.
      // Return the data object so callers can check data.session.
      return response.data.data ?? { session: null };
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /* ====================================================================
   * Rooms & LiveKit
   * ==================================================================== */

  async getRooms() {
    const cacheKey = 'rooms';
    const cached = this.getFromCache(cacheKey, 2 * 60 * 1000); // 2 minutes cache
    if (cached) return cached;

    try {
      const response = await this.client.get('/rooms');
      
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to get rooms', response.data);
      }
      
      const rooms = response.data.data;
      this.setInCache(cacheKey, rooms);
      
      return rooms;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  async getRoom(id) {
    try {
      const response = await this.client.get(`/rooms/${id}`);
      
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to get room', response.data);
      }
      
      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  async createRoom(roomData) {
    try {
      const response = await this.client.post('/rooms', roomData);
      
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to create room', response.data);
      }
      
      // Clear rooms cache since we added a new one
      this.removeFromCache('rooms');
      
      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  async generateRoomToken(roomId, participantName = null) {
    try {
      const payload = {};
      if (participantName) {
        payload.participant_name = participantName;
      }

      const response = await this.client.post(`/rooms/${roomId}/join-token`, payload);
      
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to generate token', response.data);
      }
      
      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  async sendRoomTip(roomId, tipData) {
    try {
      const response = await this.client.post(`/rooms/${roomId}/tip`, tipData);
      
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to send tip', response.data);
      }
      
      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /* ====================================================================
   * Wallet & Billing
   * ==================================================================== */

  async getWalletBalance() {
    const cacheKey = 'wallet_balance';
    const cached = this.getFromCache(cacheKey, 1 * 60 * 1000); // 1 minute cache
    if (cached) return cached;

    try {
      const response = await this.client.get('/wallet/balance?view=all');
      
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to get balance', response.data);
      }
      
      const balance = response.data.data.balances;
      this.setInCache(cacheKey, balance);
      
      return balance;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /* ====================================================================
   * Top-up packages (slice J.2)
   * ====================================================================
   *
   * Read-only catalog of wallet top-up offers. Backed by
   * GET /api/v1/payments/topup-packages which resolves the most-specific
   * (frontend_project, tenant) row per code with platform-default
   * fallback. The payload shape per package is documented in the
   * controller and asserted by tests — the FE only treats it as a
   * read-only DTO.
   */

  /**
   * Fetch the active top-up packages for the current tenant + frontend
   * project. Cached for 5 minutes (catalog rarely changes intra-session).
   *
   * @returns {Promise<{ currency: string, packages: Array, frontend_project: any, tenant: any }>}
   */
  async listTopUpPackages() {
    const cacheKey = 'topup_packages';
    const cached   = this.getFromCache(cacheKey, 5 * 60 * 1000);
    if (cached) return cached;

    try {
      const response = await this.client.get('/payments/topup-packages');
      if (!response.data.status) {
        throw new FrontendServiceError(
          response.data.message || 'Failed to list top-up packages',
          response.data
        );
      }
      const data = response.data.data || {};
      this.setInCache(cacheKey, data);
      return data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Available payment providers for the current tenant. Cached for 5
   * minutes so the InlineTopUp sheet doesn't re-fetch on every open.
   *
   * @returns {Promise<{ items: Array<{ id:number, name:string }> }>}
   */
  async listPaymentProviders() {
    const cacheKey = 'payment_providers';
    const cached   = this.getFromCache(cacheKey, 5 * 60 * 1000);
    if (cached) return cached;

    try {
      const response = await this.client.get('/payments/providers');
      if (!response.data.status) {
        throw new FrontendServiceError(
          response.data.message || 'Failed to list payment providers',
          response.data
        );
      }
      const data = response.data.data || {};
      this.setInCache(cacheKey, data);
      return data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Payment methods grouped by provider. Cached briefly so the InlineTopUp
   * sheet can pre-resolve provider + method without UI delay.
   *
   * @returns {Promise<{ items: Array<{ provider_id:number, provider_name:string, methods:Array }> }>}
   */
  async listPaymentMethods() {
    const cacheKey = 'payment_methods';
    const cached   = this.getFromCache(cacheKey, 5 * 60 * 1000);
    if (cached) return cached;

    try {
      const response = await this.client.get('/payments/methods');
      if (!response.data.status) {
        throw new FrontendServiceError(
          response.data.message || 'Failed to list payment methods',
          response.data
        );
      }
      const data = response.data.data || {};
      this.setInCache(cacheKey, data);
      return data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Create a one-time payment via POST /payments/one-time.
   *
   * Slice J.2: the preferred shape uses `package_code` so the gateway
   * resolves amount / credits_cents / bonus_cents / wallet_type server-side.
   * idempotency_key is REQUIRED on the package path. Returns the standard
   * Gateway envelope `data` (payment_id, checkout_url, status,
   * idempotent_replay, package).
   *
   * @param {object} payload
   * @param {string} payload.provider           PSP name returned by `GET /payments/providers`
   *                                            (e.g. 'mollie', 'nowpayments',
   *                                            'icepay_xplow', 'icepay_leadcenter').
   * @param {string} payload.package_code       e.g. 'popular_75'
   * @param {string} payload.idempotency_key    stable replay key
   * @param {string} payload.redirect_url       caller-supplied URL
   * @param {number=} payload.payment_method_id
   * @param {string=} payload.method            method code (e.g. 'ideal', 'bancontact')
   *
   * @returns {Promise<{payment_id:string, checkout_url:string|null, status:string, idempotent_replay:boolean, package?:any}>}
   */
  async createOneTimePayment(payload) {
    try {
      const response = await this.client.post('/payments/one-time', payload);
      if (!response.data.status) {
        throw new FrontendServiceError(
          response.data.message || 'Failed to create payment',
          response.data
        );
      }
      // Bust any cached wallet balance so the chip refreshes after the
      // user returns from the PSP.
      this.removeFromCache?.('wallet_balance');
      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /* ====================================================================
   * Cache Management
   * ==================================================================== */

  setInCache(key, value, ttl = null) {
    const expireTime = Date.now() + (ttl || this.cacheTimeout);
    this.cache.set(key, { value, expireTime });
  }

  getFromCache(key, maxAge = null) {
    const cached = this.cache.get(key);
    if (!cached) return null;

    const now = Date.now();
    const age = maxAge || this.cacheTimeout;
    
    if (now > cached.expireTime || (maxAge && now - (cached.expireTime - this.cacheTimeout) > age)) {
      this.cache.delete(key);
      return null;
    }

    return cached.value;
  }

  removeFromCache(key) {
    this.cache.delete(key);
  }

  clearCache() {
    this.cache.clear();
  }

  /* ====================================================================
   * Storage Management
   * ==================================================================== */

  setAuthToken(token) {
    if (typeof window !== 'undefined') {
      localStorage.setItem('auth_token', token);
    }
  }

  getAuthToken() {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('auth_token');
    }
    return null;
  }

  clearAuthToken() {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('auth_token');
    }
  }

  setUserData(user) {
    if (typeof window !== 'undefined') {
      localStorage.setItem('user_data', JSON.stringify(user));
    }
  }

  getUserData() {
    if (typeof window !== 'undefined') {
      const userData = localStorage.getItem('user_data');
      return userData ? JSON.parse(userData) : null;
    }
    return null;
  }

  clearUserData() {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('user_data');
    }
  }

  setTenantData(tenant) {
    persistTenantData(tenant);
  }

  getTenantData() {
    return readPersistedTenantData();
  }

  setLogoutRedirectUri(uri) {
    persistLogoutRedirectUri(uri);
  }

  getLogoutRedirectUri() {
    return readLogoutRedirectUri();
  }

  /* ====================================================================
   * Utility Methods
   * ==================================================================== */

  isAuthenticated() {
    return !!this.getAuthToken();
  }

  generateRequestId() {
    return 'req_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  handleUnauthorized() {
    const currentToken = this.getAuthToken();
    if (currentToken) {
      console.log('🔑 Token invalid, clearing auth');
      this.clearAuthToken();
      this.clearUserData();
      this.clearCache();
      
      // Only redirect if we're not already on a login page
      if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
  }

  normalizeError(error) {
    if (error instanceof FrontendServiceError) {
      return error;
    }

    if (error.response?.data) {
      const { message, data, status } = error.response.data;
      return new FrontendServiceError(
        message || error.message || 'An error occurred',
        data || {},
        error.response.status
      );
    }

    return new FrontendServiceError(error.message || 'An error occurred', {});
  }
}

/**
 * Custom error class for Frontend Service errors
 */
class FrontendServiceError extends Error {
  constructor(message, data = {}, status = null) {
    super(message);
    this.name = 'FrontendServiceError';
    this.data = data;
    this.status = status;
  }
}

// Create singleton instance
const frontendService = new FrontendService();

export default frontendService;
export { FrontendServiceError };